import React, {
    ComponentPropsWithoutRef,
    ElementType,
    PropsWithChildren,
  } from 'react';
  import { cva, type VariantProps } from 'class-variance-authority';
  import { CommonProps } from '@/utils/props';
  import { cn } from '@/utils/cn';
  import Icon from '../Icon/Icon';
  
  type PolymorphicAsProp<E extends ElementType> = {
    as?: E;
  };
  
  type PolymorphicProps<E extends ElementType> = PropsWithChildren<
    ComponentPropsWithoutRef<E> & PolymorphicAsProp<E>
  >;
  
  const defaultElement = 'button';
  
  const button = cva(
    [
      'button',
      'border-2',
      'rounded-lg',
      'outline-none',
      'text-center',
      'font-bold',
      'disabled:bg-surface-200',
      'disabled:text-surface-400',
      'disabled:border-surface-200',
      'focus:ring-2',
      'focus:ring-info-200',
      'transition-colors',
      'flex',
      'items-center',
      'align-center',
      'justify-center',
      'gap-2',
      'justify-center',
    ],
    {
      variants: {
        intent: {
          neutral: [
            'bg-surface-500',
            'border-surface-500',
            'hover:bg-surface-700',
            'hover:border-surface-700',
            'active:bg-surface-600',
            'focus:ring-info-200',
            'focus:border-surface-800',
          ],
          black:[
            'bg-surface-950',
            'border-surface-950',
            'hover:bg-surface-0',
            'hover:border-surface-950',
            'active:ring-transparent',
            'active:bg-surface-0', 
            'focus:ring-surface-950',
            'focus:border-surface-950',
          ],
          primary: [
            'bg-primary-500',
            'border-primary-500',
            'hover:bg-primary-700',
            'hover:border-primary-700',
            'active:bg-primary-600',
            'focus:bg-primary-600',
            'focus:border-primary-800',
          ],
          secondary: [
            'bg-secondary-500',
            'border-secondary-500',
            'hover:bg-secondary-700',
            'hover:border-secondary-700',
            'active:bg-secondary-600',
            'focus:bg-secondary-600',
            'focus:border-secondary-800',
          ],
        },
        size: {
          xs: ['px-4', 'py-2.5', 'text-sm'],
          sm: ['px-8', 'py-3', 'text-base'],
          md: ['px-9', 'py-4', 'text-xl'],
          lg: ['px-12', 'py-6', 'text-xl'],
        },
        variant: {
          solid: ['text-white'],
          outline: ['text-surface-950'],
          ghost: ['bg-transparent', 'text-surface-900'],
        },
      },
      compoundVariants: [
        {
          variant: ['outline'],
          className: [
            'bg-transparent',
            'hover:bg-transparent',
            'active:bg-transparent',
            'focus:bg-transparent',
            'disabled:bg-transparent',
            'hover:border-surface-500',
            'hover:text-surface-600',
          ],
        },
        {
          variant: ['ghost'],
          className: [
            'border-transparent',
            'px-2 py-1.5',
            'bg-transparent',
            'hover:bg-transparent',
            'hover:text-surface-600',
            'hover:border-transparent',
            'active:border-surface-200',
            'active:bg-surface-200',
            'active:ring-transparent',
            'focus:bg-transparent',
            'disabled:bg-transparent',
            'disabled:border-transparent',
            'h-max',
          ],
        },
      ],
      defaultVariants: {
        intent: 'neutral',
        size: 'md',
        variant: 'solid',
      },
    }
  );
  
  interface ButtonProps
    extends VariantProps<typeof button>,
      Omit<CommonProps, 'className'> {
    icon?: string | React.ReactNode;
    endIcon?: string | React.ReactNode;
    disabled?: boolean;
  }
  
  type ButtonComponentProps<E extends ElementType = typeof defaultElement> =
    PolymorphicProps<E> & ButtonProps;
  
  export function Button<E extends ElementType = typeof defaultElement>(propscomponent: ButtonComponentProps<E>) {
    const {
    as,
    className,
    intent,
    size,
    variant,
    icon,
    endIcon,
    children,
    ...props
  } = propscomponent;
    const Component = as ?? defaultElement;
     
    const renderIcon = (icon: string | React.ReactNode): React.JSX.Element => {
      if (typeof icon === 'string') {
        return <Icon iconName={icon} size={size === 'xs' ? 'xs' : 'md'} />;
      }
  
      return React.cloneElement(icon as React.ReactElement<{ disabled?: boolean }>, {
        disabled: props.disabled,
      });
    };
    return (
      <Component
        className={cn(button({ intent, variant, size }), className)}
        {...props}
      >
        {icon && renderIcon(icon)}
        <span>{children}</span>
        {endIcon && renderIcon(endIcon)}
      </Component>
    );
  }
  
  export default Button;
  